import "./styles/globals.css";
import * as RawUI from "./components/ui";
import { autoShadowModule } from "./shadow/autoShadow";

export type * from "./components/ui";
export * from "./theme";
export * from "./shadow";
export { cn } from "./lib/cn";

// Leaf / self-contained components auto-isolated in their own shadow root on use.
// Containers, overlays, layout, tables and content patterns are intentionally NOT
// shadowed — they render consumer-provided children that must keep the consuming
// app's styles (and remain composable / accessible).
//
// Components that portal overlay content to <body> (Select, SearchableSelect,
// MultiSelect, TagInput/TagsInput, SuggestInput) are also excluded: their trigger elements must stay
// in the same document scope as the portaled content so that Radix UI's
// DismissableLayer can correctly resolve outside-clicks via event.target.
// Shadow DOM retargets events to the shadow host, which breaks that check.
const AUTO_SHADOW = new Set<string>([
  "Button",
  "IconButton",
  "ButtonGroup",
  "Badge",
  "Chip",
  "Spinner",
  "Skeleton",
  "ProgressBar",
  "Label",
  "FieldLabel",
  "HelperText",
  "InlineError",
  "Input",
  "Textarea",
  "Checkbox",
  "Radio",
  "Switch",
  "Slider",
  "NumberStepper",
  "ColorPicker",
  "ColorSwatchGroup",
  "SelectableChipGroup",
  "OptionCardGroup",
  "FileDropzone",
  "RevealField",
  "RevealAndCopy",
  "Breadcrumbs",
  "FormField"
]);

const UI = autoShadowModule(RawUI, AUTO_SHADOW);

export const {
  Accordion,
  ActionMenu,
  Alert,
  Avatar,
  Badge,
  BfrsProvider,
  Box,
  Breadcrumbs,
  BusinessInfoDisplayCard,
  BusinessInfoDisplayCardSkeleton,
  Button,
  ButtonGroup,
  CUSTOM_PRESET_ID,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  ChatBubble,
  ChatComposer,
  Checkbox,
  Chip,
  Collapsible,
  ColorPicker,
  ColorSwatchGroup,
  CommandMenu,
  ConfirmDialog,
  Container,
  DAY_HEADS,
  DAY_MS,
  DataTable,
  DateRangePicker,
  Dialog,
  Drawer,
  Dropdown,
  EmptyState,
  EntityDisplayCard,
  ErrorState,
  FieldLabel,
  FileDropzone,
  FilterBar,
  FilterDrawer,
  FormField,
  FormSection,
  FullPageLoader,
  Grid,
  HorizontalStepper,
  HelperText,
  Icon,
  IconButton,
  InfoCard,
  InlineError,
  Input,
  KpiCard,
  Label,
  LayoutShell,
  ListPageLayout,
  LoadingState,
  MONTH_NAMES,
  MetricCard,
  Modal,
  MultiSelect,
  NumberStepper,
  OptionCardGroup,
  PageHeader,
  Paper,
  Popover,
  ProgressBar,
  Radio,
  RevealAndCopy,
  RevealField,
  SHORT_MONTH_NAMES,
  SearchableSelect,
  SectionHeader,
  Select,
  SelectableChipGroup,
  SideNav,
  Skeleton,
  Slider,
  Spinner,
  Stack,
  StepProgressCard,
  StepProgressCardSkeleton,
  SuggestInput,
  SummaryBar,
  Switch,
  TagInput,
  TagsInput,
  Table,
  TableBulkActions,
  TableColumnVisibility,
  TableEmptyState,
  TableErrorState,
  TablePagination,
  TableRowActions,
  TableSaveView,
  TableSkeleton,
  TableToolbar,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  Textarea,
  Tip,
  ToastContext,
  ToastManager,
  ToastProvider,
  Tooltip,
  WorkspaceHeader,
  addDays,
  compareDay,
  daysInMonth,
  defaultDateRangePresets,
  defaultFormatDate,
  differenceInCalendarDays,
  getInitialCalendar,
  getPresetLabel,
  groupSelectOptions,
  hasCompleteRange,
  normalizeRange,
  presetToRange,
  sameDay,
  shiftMonth,
  startOfDay,
  useToast
} = UI;

// Preserve the compound Tabs type in generated declarations without changing
// the runtime value selected by the Shadow DOM export facade.
export const Tabs: typeof RawUI.Tabs = UI.Tabs;
